/**
 * Person-name helpers.
 *
 * Shared because two pages need the same answer: the school landing page lists a
 * team's teachers by last name, and the plan page repeats that roster. Keeping one
 * implementation means a team reads the same way in both places.
 */

/** Generational suffixes that are never the name a parent is looking for. */
const SUFFIXES = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);

/**
 * Lowercase particles that belong to the surname rather than standing alone, so
 * "Ana de la Cruz" reads as "de la Cruz" rather than "Cruz".
 */
const PARTICLES = new Set([
  "de", "del", "della", "der", "di", "da", "das", "dos",
  "la", "le", "van", "von", "bin", "al", "st", "st.",
  // Gaelic particles are written separately and are part of the surname:
  // "Seán Ó Súilleabháin" is Ó Súilleabháin, not Súilleabháin. Mac/Mc are
  // left out deliberately — they are normally joined ("MacDonald"), and a
  // standalone "Mac" is far more likely to be a given name.
  "ó", "o'", "ní", "nic", "ua",
]);

/**
 * The surname a parent would recognise, with original capitalisation preserved.
 *
 *   "Kelly DeMuri"      -> "DeMuri"
 *   "Joe Rhodes Jr."    -> "Rhodes"
 *   "Ana de la Cruz"    -> "de la Cruz"
 *   "Krabappel"         -> "Krabappel"
 *
 * Callers that need a sort key should lowercase the result themselves; this
 * deliberately does not, because it is used for display.
 */
export function getLastName(fullName: string): string {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";

  // Drop a trailing suffix so it isn't mistaken for the surname.
  let end = parts.length - 1;
  if (end > 0 && SUFFIXES.has(parts[end].toLowerCase())) end -= 1;

  // Walk back over any particles so they stay attached to the surname.
  let start = end;
  while (start > 0 && PARTICLES.has(parts[start - 1].toLowerCase())) start -= 1;

  return parts.slice(start, end + 1).join(" ");
}

export default { getLastName };
